import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, CheckCircle } from 'lucide-react';
import './ContactPage.css';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  const contactMethods = [
    {
      icon: <Mail size={40} />,
      title: 'Email Us',
      details: [
        'info@cnterminalghana.com',
        'enquiries@cnterminalghana.com'
      ],
      description: 'For general enquiries and information'
    },
    {
      icon: <Phone size={40} />,
      title: 'Call Us',
      details: [
        '+233 553 799 811',
        '+233 506 580 292',
        '+233 555 552 645'
      ],
      description: 'Available during business hours'
    },
    {
      icon: <MapPin size={40} />,
      title: 'Visit Us',
      details: [
        'CN Terminal Company Limited',
        'Salifu Dagati Extension',
        'Tema Community Two',
        'Opposite Tema Police Station',
        'P.O. BOX TN 161',
        'Teshie-Nungua, Accra, Ghana'
      ],
      description: 'Our office location'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', time: '8:00 AM - 6:00 PM' },
    { day: 'Saturday', time: 'Closed' },
    { day: 'Sunday', time: 'Closed' }
  ];

  const services = [
    'Freight Forwarding',
    'Air Cargo',
    'Custom Clearance',
    'Warehousing',
    'Transit Services',
    'Import/Export',
    'Cargo Logistics',
    'Destination Management',
    'Haulage'
  ];

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1 className="page-title">Get In Touch</h1>
          <p className="page-subtitle">
            We're here to help with all your shipping and logistics needs. 
            Contact us today and let's discuss how we can assist your business.
          </p>
        </div>
      </div>

      <div className="container">
        <section className="contact-methods-section">
          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="contact-method-card">
                <div className="contact-method-content">
                  <div className="contact-method-icon">{method.icon}</div>
                  <h3>{method.title}</h3>
                  <p className="contact-method-description">{method.description}</p>
                  <div className="contact-method-details">
                    {method.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="contact-detail-item">
                        {index === 0 && <Mail size={16} />}
                        {index === 1 && <Phone size={16} />}
                        {index === 2 && <MapPin size={16} />}
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="contact-form-section">
          <div className="contact-form-wrapper">
            <div className="contact-form-header">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                    <option value="General Enquiry">General Enquiry</option>
                    <option value="Quote Request">Quote Request</option>
                    <option value="Support">Support</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button type="submit" className="btn btn-primary submit-btn">
                <Send size={20} />
                Send Message
              </button>

              {submitted && (
                <div className="form-success">
                  <CheckCircle size={24} />
                  <span>Thank you! Your message has been sent successfully.</span>
                </div>
              )}
            </form>
          </div>
        </section>

        <section className="office-hours-section">
          <div className="office-hours-card">
            <div className="office-hours-header">
              <Clock size={40} />
              <h2>Office Hours</h2>
            </div>
            <div className="office-hours-list">
              {officeHours.map((schedule, index) => (
                <div key={index} className="office-hour-item">
                  <span className="office-day">{schedule.day}</span>
                  <span className="office-time">{schedule.time}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="location-section">
          <div className="location-map-container">
            <div className="location-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3970.5633627049856!2d-0.012133999999999999!3d5.6312806!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdf87a4fbd96885%3A0xbcc5e569c7412865!2sCN%20Terminal%20Company%20Ltd!5e0!3m2!1sen!2sgh!4v1763126892677!5m2!1sen!2sgh"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="CN Terminal Company Limited Location"
                className="map-iframe"
              />
            </div>
          </div>
        </section>
      </div>

      <div className="contact-cta">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Let's discuss how we can help streamline your logistics operations.</p>
          <div className="cta-buttons">
            <a href="tel:+233553799811" className="btn btn-secondary">
              <Phone size={20} />
              Call Now
            </a>
            <a href="mailto:info@cnterminalghana.com" className="btn btn-primary">
              <Mail size={20} />
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
